/**
 * Copyright (c) 2018 mol* contributors, licensed under MIT, See LICENSE file for more info.
 *
 * @author David Sehnal <david.sehnal@gmail.com>
 */
import { Segmentation, SortedArray } from '../../../../../mol-data/int';
import { IntAdjacencyGraph } from '../../../../../mol-math/graph';
import { BondType } from '../../../model/types';
import { sortArray } from '../../../../../mol-data/util';
import { arraySetAdd, arraySetRemove } from '../../../../../mol-util/array';
export function computeRings(unit) {
  var size = largestResidue(unit);
  var state = State(unit, size);
  var residuesIt = Segmentation.transientSegments(unit.model.atomicHierarchy.residueAtomSegments, unit.elements);

  while (residuesIt.hasNext) {
    var seg = residuesIt.move();
    processResidue(state, seg.start, seg.end);
  }

  return state.rings;
}

function State(unit, capacity) {
  return {
    startVertex: 0,
    endVertex: 0,
    count: 0,
    visited: new Int32Array(capacity),
    queue: new Int32Array(capacity),
    pred: new Int32Array(capacity),
    left: new Int32Array(4
    /* MaxDepth */
    ),
    right: new Int32Array(4
    /* MaxDepth */
    ),
    color: new Int32Array(capacity),
    currentColor: 0,
    currentAltLoc: '',
    hasAltLoc: false,
    rings: [],
    currentRings: [],
    unit: unit,
    bonds: unit.bonds,
    altLoc: unit.model.atomicHierarchy.atoms.label_alt_id
  };
}

function resetState(state) {
  state.count = state.endVertex - state.startVertex;
  var visited = state.visited,
      pred = state.pred,
      color = state.color;

  for (var i = 0; i < state.count; i++) {
    visited[i] = -1;
    pred[i] = -1;
    color[i] = 0;
  }

  state.currentColor = 0;
  state.currentAltLoc = '';
  state.hasAltLoc = false;
}

function largestResidue(unit) {
  var residuesIt = Segmentation.transientSegments(unit.model.atomicHierarchy.residueAtomSegments, unit.elements);
  var size = 0;

  while (residuesIt.hasNext) {
    var seg = residuesIt.move();
    size = Math.max(size, seg.end - seg.start);
  }

  return size;
}

function processResidue(state, start, end) {
  var visited = state.visited;
  state.startVertex = start;
  state.endVertex = end; // no two atom rings

  if (state.endVertex - state.startVertex < 3) return;
  state.currentRings = [];
  var elements = state.unit.elements;
  var altLocs = [];

  for (var i = state.startVertex; i < state.endVertex; i++) {
    var altLoc = state.altLoc.value(elements[i]);
    arraySetAdd(altLocs, altLoc);
  }

  arraySetRemove(altLocs, '');

  if (altLocs.length === 0) {
    resetState(state);

    for (var i = 0; i < state.count; i++) {
      if (visited[i] >= 0) continue;
      findRings(state, i);
    }
  } else {
    for (var aI = 0; aI < altLocs.length; aI++) {
      resetState(state);
      state.hasAltLoc = true;
      state.currentAltLoc = altLocs[aI];

      for (var i = 0; i < state.count; i++) {
        if (visited[i] >= 0) continue;
        var altLoc = state.altLoc.value(elements[state.startVertex + i]);

        if (altLoc && altLoc !== state.currentAltLoc) {
          continue;
        }

        findRings(state, i);
      }
    }
  }

  for (var i = 0, _i = state.currentRings.length; i < _i; i++) {
    state.rings.push(state.currentRings[i]);
  }
}

function addRing(state, a, b) {
  // only "monotonous" rings
  if (b < a) {
    return;
  }

  var pred = state.pred,
      color = state.color,
      left = state.left,
      right = state.right;
  var nc = ++state.currentColor;
  var current = a;

  for (var t = 0; t < 4
  /* MaxDepth */
  ; t++) {
    color[current] = nc;
    current = pred[current];
    if (current < 0) break;
  }

  var leftOffset = 0,
      rightOffset = 0;
  var found = false,
      target = 0;
  current = b;

  for (var t = 0; t < 4
  /* MaxDepth */
  ; t++) {
    if (color[current] === nc) {
      target = current;
      found = true;
      break;
    }

    right[rightOffset++] = current;
    current = pred[current];
    if (current < 0) break;
  }

  if (!found) {
    return;
  }

  current = a;

  for (var t = 0; t < 4
  /* MaxDepth */
  ; t++) {
    left[leftOffset++] = current;
    if (target === current) break;
    current = pred[current];
    if (current < 0) break;
  }

  var len = leftOffset + rightOffset; // rings must have at least three elements

  if (len < 3) {
    return;
  }

  var ring = new Int32Array(len);
  var ringOffset = 0;

  for (var t = 0; t < leftOffset; t++) ring[ringOffset++] = state.startVertex + left[t];

  for (var t = rightOffset - 1; t >= 0; t--) ring[ringOffset++] = state.startVertex + right[t];

  sortArray(ring);

  if (state.hasAltLoc) {
    // we need to check if the ring was already added because alt locs are present.
    for (var rI = 0, _rI = state.currentRings.length; rI < _rI; rI++) {
      var r = state.currentRings[rI];
      if (ring[0] !== r[0]) continue;
      if (ring.length !== r.length) continue;
      var areSame = true;

      for (var aI = 0, _aI = ring.length; aI < _aI; aI++) {
        if (ring[aI] !== r[aI]) {
          areSame = false;
          break;
        }
      }

      if (areSame) {
        return;
      }
    }
  }

  state.currentRings.push(SortedArray.ofSortedArray(ring));
}

function findRings(state, from) {
  var bonds = state.bonds,
      startVertex = state.startVertex,
      endVertex = state.endVertex,
      visited = state.visited,
      queue = state.queue,
      pred = state.pred;
  var elements = state.unit.elements;
  var neighbor = bonds.b,
      bondFlags = bonds.edgeProps.flags,
      offset = bonds.offset;
  visited[from] = 1;
  queue[0] = from;
  var head = 0,
      size = 1;

  while (head < size) {
    var top_1 = queue[head++];
    var a = startVertex + top_1;
    var start = offset[a],
        end = offset[a + 1];

    for (var i = start; i < end; i++) {
      var b = neighbor[i];
      if (b < startVertex || b >= endVertex || !BondType.isCovalent(bondFlags[i])) continue;

      if (state.hasAltLoc) {
        var altLoc = state.altLoc.value(elements[b]);

        if (altLoc && state.currentAltLoc !== altLoc) {
          continue;
        }
      }

      var other = b - startVertex;

      if (visited[other] > 0) {
        if (pred[other] !== top_1 && pred[top_1] !== other) {
          addRing(state, top_1, other);
        }

        continue;
      }

      visited[other] = 1;
      queue[size++] = other;
      pred[other] = top_1;
    }
  }
}

export function getFingerprint(elements) {
  var len = elements.length;
  var reversed = new Array(len);

  for (var i = 0; i < len; i++) reversed[i] = elements[len - i - 1];

  var rotNormal = getMinimalRotation(elements);
  var rotReversed = getMinimalRotation(reversed);
  var isNormalSmaller = false;

  for (var i = 0; i < len; i++) {
    var u = elements[(i + rotNormal) % len],
        v = reversed[(i + rotReversed) % len];

    if (u !== v) {
      isNormalSmaller = u < v;
      break;
    }
  }

  if (isNormalSmaller) return buildFinderprint(elements, rotNormal);
  return buildFinderprint(reversed, rotReversed);
}

function getMinimalRotation(elements) {
  // adapted from http://en.wikipedia.org/wiki/Lexicographically_minimal_string_rotation
  var len = elements.length;
  var f = new Int32Array(len * 2);

  for (var i = 0; i < f.length; i++) f[i] = -1;

  var u = '',
      v = '',
      k = 0;

  for (var j = 1; j < f.length; j++) {
    var i = f[j - k - 1];

    while (i !== -1) {
      u = elements[j % len];
      v = elements[(k + i + 1) % len];
      if (u === v) break;
      if (u < v) k = j - i - 1;
      i = f[i];
    }

    if (i === -1) {
      u = elements[j % len];
      v = elements[(k + i + 1) % len];

      if (u !== v) {
        if (u < v) k = j;
        f[j - k] = -1;
      } else f[j - k] = i + 1;
    } else f[j - k] = i + 1;
  }

  return k;
}

function buildFinderprint(elements, offset) {
  var len = elements.length;
  var ret = [];
  var i;

  for (i = 0; i < len - 1; i++) {
    ret.push(elements[(i + offset) % len]);
    ret.push('-');
  }

  ret.push(elements[(i + offset) % len]);
  return ret.join('');
}

export function createIndex(rings, aromaticRings) {
  var elementRingIndices = new Map();
  var elementAromaticRingIndices = new Map(); // for each ring atom, assign all rings that it is present in

  for (var rI = 0, _rI = rings.length; rI < _rI; rI++) {
    var r = rings[rI];

    for (var i = 0, _i = r.length; i < _i; i++) {
      var e = r[i];
      if (elementRingIndices.has(e)) elementRingIndices.get(e).push(rI);else elementRingIndices.set(e, [rI]);
    }
  } // for each ring atom, assign all aromatic rings that it is present in


  for (var aI = 0, _aI = aromaticRings.length; aI < _aI; aI++) {
    var rI = aromaticRings[aI];
    var r = rings[rI];

    for (var i = 0, _i = r.length; i < _i; i++) {
      var e = r[i];
      if (elementAromaticRingIndices.has(e)) elementAromaticRingIndices.get(e).push(rI);else elementAromaticRingIndices.set(e, [rI]);
    }
  } // create a graph where vertices are rings, edge if two rings share at least one atom


  var graph = new IntAdjacencyGraph.UniqueEdgeBuilder(rings.length);

  for (var rI = 0, _rI = rings.length; rI < _rI; rI++) {
    var r = rings[rI];

    for (var i = 0, _i = r.length; i < _i; i++) {
      var e = r[i];
      var containedRings = elementRingIndices.get(e);
      if (containedRings.length === 1) continue;

      for (var j = 0, _j = containedRings.length; j < _j; j++) {
        var rJ = containedRings[j];
        if (rI >= rJ) continue;
        graph.addEdge(rI, rJ);
      }
    }
  }

  var components = IntAdjacencyGraph.connectedComponents(graph.getGraph());
  var ringComponentIndex = components.componentIndex;
  var ringComponents = [];

  for (var i = 0; i < components.componentCount; i++) ringComponents[i] = [];

  for (var rI = 0, _rI = rings.length; rI < _rI; rI++) {
    ringComponents[ringComponentIndex[rI]].push(rI);
  }

  return {
    elementRingIndices: elementRingIndices,
    elementAromaticRingIndices: elementAromaticRingIndices,
    ringComponentIndex: ringComponentIndex,
    ringComponents: ringComponents
  };
}